/**
 * Grid Tab 往下優先 Helper
 * 處理 Tab 鍵在 Grid 編輯模式的導航邏輯
 *
 * 用法：
 * import { handleGridTab, focusGridInput } from '../../shared/grid-tab-helper';
 *
 * onInputKeydown(event: KeyboardEvent, dataItem: MyDto, field: string) {
 *   if (event.key !== 'Tab') return;
 *
 *   const result = handleGridTab(event, {
 *     items: this.filteredItems,
 *     currentItem: dataItem,
 *     currentField: field,
 *     fields: ['qty', 'cost'],
 *     getId: (item) => item.Id,
 *     isEditable: (item) => !item.IsOpen
 *   });
 *
 *   if (result) {
 *     event.preventDefault();
 *     focusGridInput(result.itemId, result.field);
 *   }
 * }
 */

/**
 * Grid Tab 導航配置
 */
export interface GridTabConfig<T> {
  /** 資料陣列 */
  items: T[];
  /** 當前項目 */
  currentItem: T;
  /** 當前欄位名稱 */
  currentField: string;
  /** 欄位順序陣列（如 ['qty', 'cost']） */
  fields: string[];
  /** 取得項目 ID 的函數（可返回 number 或 string） */
  getId: (item: T) => number | string;
  /** 判斷項目是否可編輯（可選，預設全部可編輯）。
   *  第二參數為目標欄位，供「部分列才有輸入框」的欄位（如批號/效期）依欄位過濾，不需要時可省略 */
  isEditable?: (item: T, field: string) => boolean;
  /** Tab 方向：'down' 往下優先，'right' 使用瀏覽器預設（可選，預設 'down'） */
  direction?: 'down' | 'right';
}

/**
 * 導航結果
 */
export interface GridTabResult {
  /** 目標項目 ID（可以是 number 或 string） */
  itemId: number | string;
  /** 目標欄位 */
  field: string;
}

/**
 * 處理 Tab 鍵事件，回傳下一個應該聚焦的元素資訊
 *
 * 導航邏輯（往下優先）：
 * - Tab: 往下跳到同欄位的下一行，到底則跳到下一欄位的第一行
 * - Shift+Tab: 往上跳到同欄位的上一行，到頂則跳到上一欄位的最後一行
 *
 * @param event 鍵盤事件
 * @param config 配置
 * @returns 導航結果，若無法導航則回傳 null
 */
export function handleGridTab<T>(
  event: KeyboardEvent,
  config: GridTabConfig<T>
): GridTabResult | null {
  const { items, currentItem, currentField, fields, getId, isEditable, direction = 'down' } = config;

  // 向右模式：不攔截，使用瀏覽器預設行為
  if (direction === 'right') {
    return null;
  }

  if (!items || items.length === 0) {
    return null;
  }

  // 取得「某欄位」的可編輯項目（欄位如批號/效期只有部分列有輸入框，需依欄位各自過濾）
  const editableFor = (field: string): T[] =>
    isEditable ? items.filter(x => isEditable(x, field)) : items;

  // 找出當前項目在當前欄位可編輯項目中的索引
  const currentItems = editableFor(currentField);
  const currentId = getId(currentItem);
  const currentIndex = currentItems.findIndex(x => getId(x) === currentId);
  if (currentIndex === -1) {
    return null;
  }

  // 找出當前欄位在欄位順序中的索引
  const currentFieldIndex = fields.indexOf(currentField);
  if (currentFieldIndex === -1) {
    return null;
  }

  if (event.shiftKey) {
    // Shift+Tab: 往上跳
    if (currentIndex > 0) {
      return {
        itemId: getId(currentItems[currentIndex - 1]),
        field: currentField
      };
    }
    // 第一行：往前找「有可編輯列」的欄位，跳到其最後一行（循環；跳過整欄不可編輯的欄位）
    for (let i = 1; i <= fields.length; i++) {
      const field = fields[(currentFieldIndex - i + fields.length) % fields.length];
      const list = editableFor(field);
      if (list.length > 0) {
        return { itemId: getId(list[list.length - 1]), field };
      }
    }
    return null;
  } else {
    // Tab: 往下跳
    if (currentIndex < currentItems.length - 1) {
      return {
        itemId: getId(currentItems[currentIndex + 1]),
        field: currentField
      };
    }
    // 最後一行：往後找「有可編輯列」的欄位，跳到其第一行（循環；跳過整欄不可編輯的欄位）
    for (let i = 1; i <= fields.length; i++) {
      const field = fields[(currentFieldIndex + i) % fields.length];
      const list = editableFor(field);
      if (list.length > 0) {
        return { itemId: getId(list[0]), field };
      }
    }
    return null;
  }
}

/**
 * 聚焦到指定的輸入框並選取內容
 *
 * @param itemId 項目 ID（可以是 number 或 string）
 * @param field 欄位名稱
 * @param idPrefix 可選的 ID 前綴（預設使用 field 作為前綴）
 */
export function focusGridInput(itemId: number | string, field: string, idPrefix?: string): void {
  // 使用較長的延遲確保 Angular 完成 DOM 渲染
  // 特別是當新增行觸發時，DOM 可能還沒準備好
  const tryFocus = (retryCount: number) => {
    const prefix = idPrefix || field;
    const inputId = `${prefix}-${itemId}`;
    const input = document.getElementById(inputId) as HTMLInputElement;
    if (input) {
      input.focus();
      // 一般 input 才有 select(); datepicker 等自訂元件 host 的 focus 已委派到內部 input 並選取內容,
      // host 本身沒有 select() 方法, 直接呼叫會丟錯, 故先判斷
      if (typeof input.select === 'function') {
        input.select();
      }
    } else if (retryCount > 0) {
      // 元素還不存在，等待 DOM 更新後重試
      setTimeout(() => tryFocus(retryCount - 1), 50);
    }
  };

  // 延遲執行，並最多重試 3 次
  setTimeout(() => tryFocus(3), 10);
}
